import { Alert, AlertDescription, AlertTitle } from '@monksflow/monks-ui';
import { TriangleAlertIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  approvalStepShortfalls,
  type MembershipSnapshot,
} from '../../lib/approval-step-shortfalls';
import type { ProjectApprovalChains } from '../../lib/useProjectApprovalChains';

interface ApprovalChainImpactNoticeProps {
  chains: ProjectApprovalChains;
  before: MembershipSnapshot;
  after: MembershipSnapshot;
}

/**
 * Which approval steps the staged change would leave without enough approvers. Advice only:
 * saving stays open. Nothing shows while the chains load, since the warning appears on its own
 * once they arrive; a failed read says so, because silence would read as "no step affected".
 */
export function ApprovalChainImpactNotice(props: ApprovalChainImpactNoticeProps) {
  const { t } = useTranslation('projects');
  const { chains } = props;

  if (chains.kind === 'loading') return null;
  if (chains.kind === 'failed') {
    return (
      <p className="text-xs text-foreground-subtle" data-testid="approval-chain-impact-unchecked">
        {t('manageProjectMembers.approvalChainImpactNotice.couldNotCheck')}
      </p>
    );
  }

  const shortfalls = approvalStepShortfalls(chains.chains, props.before, props.after);
  if (shortfalls.length === 0) return null;

  return (
    <Alert
      variant="default"
      icon={<TriangleAlertIcon className="text-warning-foreground" />}
      data-testid="approval-chain-impact"
    >
      <AlertTitle>{t('manageProjectMembers.approvalChainImpactNotice.stepsLeftShort')}</AlertTitle>
      <AlertDescription>
        <ul className="list-disc pl-md">
          {shortfalls.map((shortfall) => (
            <li key={shortfall.stepId}>
              {t('manageProjectMembers.approvalChainImpactNotice.stepApprovers', {
                chain: shortfall.chainName,
                step: shortfall.stepName,
                available: shortfall.available,
                count: shortfall.required,
              })}
            </li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );
}
